import {put, call} from 'redux-saga/effects';
import Address from "../../entities/address";
import * as Actions from "../actions/addressActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type AddressAction = {
    addressId: string;
    address: Address;
    addresses: Address[];
};

export function* fetchOneAddress(action: AddressAction) {
	try {
		const address: Address = yield call(api.findOne, 'address', action.addressId);

		// Now we can update the user interface
		yield put({type: Actions.ADDRESS_FETCH_ONE_SUCCESS, addressId: action.addressId, address});
	}
	catch(err) {
		yield put({type: Actions.ADDRESS_FETCH_ONE_FAILURE, addressId: action.addressId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {AddressAction} action
 */
export function* saveAddress(action: AddressAction) {
    // console.debug('⮂ ADDRESS_SAVE saga', action.address?.id);
    
	if (action.address === undefined || action.address === null) {
		console.error('saveAddress received a null');
		throw new Error('saveAddress received a null');
	}

	if (!(action.address instanceof Address)) {
		action.address = new Address(action.address);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'address', action.address);
		if (addResult.isErr()) {
			console.error(`⮂ saveAddress: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ADDRESS_SAVE_FAILURE, address: action.address, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ADDRESS_SAVE, address: action.address});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'address', action.address.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.ADDRESS_SAVE_FAILURE, address: action.address, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {AddressAction} action
 * @param {Address[]} action.addresses
 */
export function* saveManyAddresses(action: AddressAction) {
    // console.debug('⮂ ADDRESS_SAVE_MANY saga', action.addresses.map(address => address.id).join(','));
    
    if (!Array.isArray(action.addresses)) {
        console.error('saveManyAddresses expected an array, received', action.addresses);
        throw new Error('saveManyAddresses expected an array');
    }

	try {
		const toSave: Address[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.addresses.length; i++) {
			const address = action.addresses[i];
			address.persisted = false;
			toSave.push(address);
		}

		const addResult = yield call(idb.add, 'address', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyAddresses: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ADDRESS_SAVE_MANY_FAILURE, addresses: action.addresses, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ADDRESS_SAVE_MANY, addresses: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.ADDRESS_SAVE_MANY_FAILURE, addresses: action.addresses, status: 500, err});

		console.error(`Error saving ${action.addresses.length} addresses to database`, err);
	}
}

/**
 * @param {AddressAction} action
 */
export function* removeAddress(action: AddressAction) {
    const userId = getCurrentUserId();
	if (!userId || action.address === undefined || action.address === null || !(action.address instanceof Address)) {
			console.error('removeAddress expected type Address, received', action.address);
			throw new Error('removeAddress expected type Address');
		}

	try {
		// Mark as deleted
		action.address.status = 'D';
		action.address.addedOn = newHRTimestamp();
		action.address.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'address', action.address);
		if (addResult.isErr()) {
			console.error(`⮂ removeAddress: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ADDRESS_REMOVE_FAILURE, address: action.address, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ADDRESS_REMOVE, address: action.address});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.ADDRESS_REMOVE_FAILURE, address: action.address, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Address.
	*
	* @param {AddressAction} action
	*/
export function* restoreAddress(action: AddressAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreAddress expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.address === undefined || action.address === null) {
        console.error('restoreAddress expected a Address, received null');
        throw new Error('restoreAddress expected a Address, received null');
    }
    else if (!(action.address instanceof Address)) action.address = new Address(action.address); 
    
	try {
		// Mark as restored
		action.address.status = 'U';
		action.address.addedOn = newHRTimestamp();
		action.address.addedBy = userId;
		action.address.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'address', action.address);
		if (addResult.isErr()) {
			console.error(`⮂ restoreAddress: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ADDRESS_RESTORE_FAILURE, address: action.address, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ADDRESS_RESTORE, address: action.address});
	}
	catch(err) {
		yield put({type: Actions.ADDRESS_RESTORE_FAILURE, address: action.address, status: 500, err});

		console.error('Error restoring from database', err);
	}
}